/**
 * Singleton Factory for API Services
 *
 * Provides a reusable pattern for creating singleton API service instances.
 * This eliminates the need to manually implement getInstance() in every API class.
 *
 * @example
 * ```typescript
 * // Before (manual singleton - 10+ lines per file):
 * export class ContactApi {
 *   private static instance: ContactApi | null = null;
 *
 *   public static getInstance(): ContactApi {
 *     if (!ContactApi.instance) {
 *       ContactApi.instance = new ContactApi();
 *     }
 *     return ContactApi.instance;
 *   }
 * }
 * export const contactApi = ContactApi.getInstance();
 *
 * // After (using factory - 2 lines):
 * export class ContactApi { ... }
 * export const contactApi = createSingletonApi(ContactApi);
 * ```
 */

/**
 * Creates a singleton instance of an API service class.
 *
 * The singleton pattern ensures only one instance of the API service exists,
 * which is beneficial for:
 * - Consistent request caching across the application
 * - Shared configuration and state
 * - Memory efficiency
 *
 * @template T - The API service class type
 * @param ApiClass - The constructor of the API service class
 * @returns A singleton instance of the API service
 *
 * @example
 * ```typescript
 * import { createSingletonApi } from './singleton-factory';
 *
 * class MyApi {
 *   fetchData() { ... }
 * }
 *
 * export const myApi = createSingletonApi(MyApi);
 *
 * // Usage in components:
 * const data = await myApi.fetchData();
 * ```
 */
export function createSingletonApi<T extends object>(ApiClass: new () => T): T {
  let instance: T | null = null

  // Use a Proxy to lazily create the instance on first access
  return new Proxy({} as T, {
    get(_target, prop: string | symbol) {
      // Create the instance on first property access
      if (!instance) {
        instance = new ApiClass()
      }

      // Get the property from the actual instance
      const value = instance[prop as keyof T]

      // Bind methods to preserve 'this' context
      if (typeof value === 'function') {
        return (value as (...args: unknown[]) => unknown).bind(instance)
      }

      return value
    },
  })
}

/**
 * Creates a singleton instance with explicit getInstance() method.
 *
 * This variant adds a static-like getInstance() method to the API class,
 * maintaining backward compatibility with the old pattern while using
 * the factory internally.
 *
 * @template T - The API service class type
 * @param ApiClass - The constructor of the API service class
 * @returns An object with getInstance() method that returns the singleton
 *
 * @example
 * ```typescript
 * class MyApi {
 *   fetchData() { ... }
 * }
 *
 * export const MyApiSingleton = createSingletonApiWithMethod(MyApi);
 * export const myApi = MyApiSingleton.getInstance();
 *
 * // Or use directly:
 * MyApiSingleton.getInstance().fetchData();
 * ```
 */
export function createSingletonApiWithMethod<T extends object>(
  ApiClass: new () => T,
): {
  getInstance(): T
} {
  const instance = createSingletonApi(ApiClass)

  return {
    getInstance: () => instance,
  }
}
